import { FC, useEffect, useRef } from 'react'

import { useOutsideClick } from '@sequencehq/utils'
import { Provider, useAtom } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import {
  FieldValidationError,
  GreyGrey30,
  RichTextEditor,
  Box,
  Flex
} from '@sequencehq/core-components'
import { ValidationResult } from 'Automations/nodeDefinitions/validation'
import { FieldRow } from './FieldRow'
import { FieldToggles } from './FieldToggles'
import { isBccVisibleAtom, isCcVisibleAtom } from './emailForm.atoms'

type EmailFormValidationErrors = {
  to?: ValidationResult[]
  cc?: ValidationResult[]
  bcc?: ValidationResult[]
  subject?: ValidationResult[]
  body?: ValidationResult[]
}

type Props = {
  to: string[]
  setTo: (value: string[]) => void
  cc: string[]
  setCc: (value: string[]) => void
  bcc: string[]
  setBcc: (value: string[]) => void
  subject: string
  setSubject: (value: string) => void
  body: string
  setBody: (value: string) => void
  recipientVariables?: Record<string, string>
  subjectVariables?: Record<string, string>
  bodyVariables?: Record<string, string>
  bodyLinkVariables?: Record<string, string>
  bodyHeight?: string
  validationErrors?: EmailFormValidationErrors
  autoFocus?: boolean
}

export const EmailFormComponent: FC<Props> = ({
  to,
  setTo,
  cc,
  setCc,
  bcc,
  setBcc,
  subject,
  setSubject,
  body,
  setBody,
  recipientVariables,
  subjectVariables,
  bodyVariables,
  bodyLinkVariables,
  bodyHeight,
  validationErrors,
  autoFocus = true
}) => {
  useHydrateAtoms([
    [isCcVisibleAtom, cc.length > 0],
    [isBccVisibleAtom, bcc.length > 0]
  ])

  const [isCcVisible, setIsCcVisible] = useAtom(isCcVisibleAtom)
  const [isBccVisible, setIsBccVisible] = useAtom(isBccVisibleAtom)

  // RichTextEditor reflects external `content` changes itself (e.g. recipients
  // prefilled from an upstream reminder once the node is connected), so the
  // editors take the values directly. CC/BCC only mount when visible, though,
  // so reveal them when they gain a value externally, matching the initial
  // hydration — otherwise a prefilled CC/BCC would stay hidden until reopened.
  useEffect(() => {
    if (cc.length > 0) setIsCcVisible(true)
  }, [cc, setIsCcVisible])
  useEffect(() => {
    if (bcc.length > 0) setIsBccVisible(true)
  }, [bcc, setIsBccVisible])

  // A CC/BCC editor only mounts when its field becomes visible. Focus it on
  // mount — that's the user opening the field — except on the form's initial
  // render, where a pre-filled field shouldn't steal focus from the page.
  const isInitialRender = useRef(true)
  useEffect(() => {
    isInitialRender.current = false
  }, [])

  const recipientRegionRef = useRef<HTMLDivElement>(null)

  const collapseEmptyRecipientFields = () => {
    // The variable menu is portaled to document.body but keeps the editor
    // focused while open, so clicking it counts as a click outside the region.
    // Don't collapse while focus is still within the region — otherwise the
    // field unmounts before the selected variable is committed.
    if (recipientRegionRef.current?.contains(document.activeElement)) return
    if (cc.length === 0) setIsCcVisible(false)
    if (bcc.length === 0) setIsBccVisible(false)
  }

  // Collapse empty CC/BCC fields once the user clicks anywhere outside the
  // recipient region (To/CC/BCC/Subject). Only listen while a field is open,
  // which also avoids collapsing the field on the same click that opened it
  // via the toggle.
  useOutsideClick({
    ref: recipientRegionRef,
    enabled: isCcVisible || isBccVisible,
    handler: collapseEmptyRecipientFields
  })

  return (
    <Flex height="100%" width="100%" flexDir="column" overflow="hidden">
      <Flex ref={recipientRegionRef} flexDir="column" width="100%">
        <FieldRow
          label="To"
          validationErrors={validationErrors?.to}
          data-testid="automations.editor.configForm.reminder.email.to.error"
        >
          <Flex flex="1" width="100%" overflow="hidden">
            <RichTextEditor
              data-testid="automations.editor.configForm.reminder.email.to"
              content={to}
              setContent={setTo}
              variant="EMAIL"
              variables={recipientVariables}
              allowVariableUseOnce
              autoFocus={autoFocus}
            />
          </Flex>

          <FieldToggles />
        </FieldRow>

        {isCcVisible && (
          <FieldRow
            label="CC"
            validationErrors={validationErrors?.cc}
            data-testid="automations.editor.configForm.reminder.email.cc.error"
          >
            <RichTextEditor
              data-testid="automations.editor.configForm.reminder.email.cc"
              content={cc}
              setContent={setCc}
              variant="EMAIL"
              variables={recipientVariables}
              allowVariableUseOnce
              autoFocus={!isInitialRender.current}
            />
          </FieldRow>
        )}

        {isBccVisible && (
          <FieldRow
            label="BCC"
            validationErrors={validationErrors?.bcc}
            data-testid="automations.editor.configForm.reminder.email.bcc.error"
          >
            <RichTextEditor
              data-testid="automations.editor.configForm.reminder.email.bcc"
              content={bcc}
              setContent={setBcc}
              variant="EMAIL"
              variables={recipientVariables}
              allowVariableUseOnce
              autoFocus={!isInitialRender.current}
            />
          </FieldRow>
        )}

        <Separator />

        <FieldRow
          validationErrors={validationErrors?.subject}
          data-testid="automations.editor.configForm.reminder.email.subject.error"
        >
          <RichTextEditor
            data-testid="automations.editor.configForm.reminder.email.subject"
            content={subject}
            setContent={setSubject}
            variant="SINGLE_LINE"
            variables={subjectVariables}
            placeholder="Subject"
            formattingEnabled={false}
            outputFormat="TEXT"
          />
        </FieldRow>

        <Separator />
      </Flex>

      <Flex
        flexDir="column"
        height={bodyHeight}
        width="100%"
        paddingX="4px"
        paddingY="6px"
        gap="4px"
        overflow="hidden"
      >
        <Flex flex="1" width="100%" overflow="hidden">
          <RichTextEditor
            data-testid="automations.editor.configForm.reminder.email.body"
            content={body}
            setContent={setBody}
            variables={bodyVariables}
            linkVariables={bodyLinkVariables}
            placeholder="Type { for variables"
          />
        </Flex>

        <FieldValidationError
          validationErrors={validationErrors?.body}
          data-testid="automations.editor.configForm.reminder.email.body.error"
        />
      </Flex>
    </Flex>
  )
}

const Separator: FC = () => (
  <Box width="100%" height="1px" backgroundColor={GreyGrey30} />
)

export const EmailForm: FC<Props> = props => {
  return (
    <Provider>
      <EmailFormComponent {...props} />
    </Provider>
  )
}
